import { useEffect } from "react";

/**
 * SEO — lightweight head-tag manager for a React SPA.
 *
 * Updates document.title and the relevant <meta> / <link rel="canonical">
 * tags directly via the DOM. We deliberately avoid pulling in
 * `react-helmet-async` (extra 6 kB + provider boilerplate) because we only
 * need to mutate ~10 tags on a handful of public routes.
 *
 * Usage:
 *   <SEO
 *     title="Webinarii AI pentru educație | FPED"
 *     description="..."
 *     canonical="https://ai.fped.org/webinarii"
 *   />
 *
 * Add `noindex` to keep transactional / admin routes out of Google:
 *   <SEO noindex title="Finalizare comandă" />
 *
 * Props (all optional):
 *   title         — replaces document.title.
 *   description   — sets <meta name="description"> + og:description + twitter:description.
 *   canonical     — sets <link rel="canonical">, og:url. Must be an absolute URL on `ai.fped.org`.
 *   image         — overrides og:image / twitter:image.
 *   noindex       — sets robots to "noindex, nofollow".
 *   ogType        — overrides og:type (default `website`).
 *   jsonLd        — an object injected as a <script type="application/ld+json">
 *                   (e.g. a schema.org `Event`). Removed on unmount / when null.
 *
 * Staging guard: when the runtime hostname is NOT `ai.fped.org`, robots is
 * forced to `noindex, nofollow` regardless of props — so preview / staging
 * deploys never accidentally leak into Google's index.
 */

const SITE_ORIGIN = "https://ai.fped.org";
const PRODUCTION_HOSTNAME = "ai.fped.org";

const DEFAULTS = {
  title: "Introducere în Inteligența Artificială pentru educație | FPED",
  description: "Curs online pentru cadre didactice despre utilizarea inteligenței artificiale în pregătire, predare și evaluare. Program dezvoltat de Fundația pentru Educație Digitală.",
  image: `${SITE_ORIGIN}/og-image.jpg`,
};

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => k !== "content" && el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute("content", attrs.content ?? "");
  return el;
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

const JSONLD_ATTR = "data-seo-jsonld";

function upsertJsonLd(data) {
  let el = document.head.querySelector(`script[${JSONLD_ATTR}]`);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute(JSONLD_ATTR, "true");
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function SEO({
  title,
  description,
  canonical,
  image,
  noindex = false,
  ogType = "website",
  jsonLd = null,
}) {
  useEffect(() => {
    const isStaging = typeof window !== "undefined" && window.location.hostname !== PRODUCTION_HOSTNAME;
    const effectiveTitle = title || DEFAULTS.title;
    const effectiveDesc = description || DEFAULTS.description;
    const effectiveImage = image || DEFAULTS.image;
    const effectiveCanonical = canonical || `${SITE_ORIGIN}${window.location.pathname}`;
    const effectiveRobots = isStaging || noindex ? "noindex, nofollow" : "index, follow";

    document.title = effectiveTitle;

    upsertMeta('meta[name="description"]', { name: "description", content: effectiveDesc });
    upsertMeta('meta[name="robots"]', { name: "robots", content: effectiveRobots });
    upsertLink("canonical", effectiveCanonical);

    upsertMeta('meta[property="og:title"]',       { property: "og:title",       content: effectiveTitle });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: effectiveDesc });
    upsertMeta('meta[property="og:url"]',         { property: "og:url",         content: effectiveCanonical });
    upsertMeta('meta[property="og:image"]',       { property: "og:image",       content: effectiveImage });
    upsertMeta('meta[property="og:type"]',        { property: "og:type",        content: ogType });

    upsertMeta('meta[name="twitter:title"]',       { name: "twitter:title",       content: effectiveTitle });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: effectiveDesc });
    upsertMeta('meta[name="twitter:image"]',       { name: "twitter:image",       content: effectiveImage });

    // Structured data (e.g. schema.org Event for a webinar detail page).
    // Skipped on staging/preview so test deploys never emit indexable markup.
    upsertJsonLd(isStaging ? null : jsonLd);
    return () => upsertJsonLd(null);
  }, [title, description, canonical, image, noindex, ogType, jsonLd]);

  return null;
}
